import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { App } from 'antd';
import { AssetService } from '~/app/services/asset/asset.service';
import {
  configurationRowsFromSnapshot,
  filterConfigurationRows,
} from '../utils';

export function useAssetOcppConfiguration(
  organizationId: string,
  assetId: string,
) {
  const { t } = useTranslation('assets');
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');

  const query = useQuery({
    queryKey: ['assets', 'ocpp-configuration', assetId],
    queryFn: () => AssetService.getOcppConfiguration(organizationId, assetId),
    enabled: Boolean(assetId) && Boolean(organizationId),
  });

  const refreshMutation = useMutation({
    mutationFn: () =>
      AssetService.refreshOcppConfiguration(organizationId, assetId),
    onSuccess: (data) => {
      queryClient.setQueryData(['assets', 'ocpp-configuration', assetId], data);
      void message.success(
        t(
          'detail.ocppConfiguration.refreshSuccess',
          'Configuration refreshed from charger',
        ),
      );
    },
    onError: (error: unknown) => {
      const detail =
        error instanceof Error
          ? error.message
          : t(
              'detail.ocppConfiguration.refreshFailed',
              'Failed to refresh configuration from charger',
            );
      void message.error(detail);
    },
  });

  const rows = useMemo(
    () =>
      filterConfigurationRows(
        configurationRowsFromSnapshot(query.data?.snapshot ?? null),
        search,
      ),
    [query.data?.snapshot, search],
  );

  return {
    search,
    setSearch,
    rows,
    snapshot: query.data?.snapshot ?? null,
    fetchedAt: query.data?.configurationFetchedAt
      ? String(query.data.configurationFetchedAt)
      : null,
    isLoading: query.isLoading || query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    refresh: refreshMutation.mutate,
    isRefreshing: refreshMutation.isPending,
  };
}
